import * as React from 'react';

import { formatAsElementId } from './Utils';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';

import { EditorView, basicSetup } from 'codemirror';
import { xml } from '@codemirror/lang-xml';
import { css } from '@codemirror/lang-css';


interface Props<T> extends React.HTMLProps<CodeEditor<T>> {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: string;
	// Para soportar más modos habría que referenciarlos en index.js para que estén disponibles
	mode: 'xml' | 'css';
	onValueChanged?: (value: string) => void;
	formId?: string;
}

interface State {
	value?: string;
	errorMsg?: string;
}

export class CodeEditor<T> extends React.Component<Props<T>, State> implements FormComponent {

	private textAreaElement = React.createRef<HTMLTextAreaElement>();
	private codeMirrorEditor: Nullable<EditorView> = null;

	constructor(props: Props<T>) {
		super(props);

		this.state = {
			value: props.boundValue,
			errorMsg: ''
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<any> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.codeMirrorEditor!.state.doc.toString());
	}

	componentDidMount() {

		// OJO: Si no ponemos un timeout, no funciona en los modales. Esto nos ha pasado al editar el css de las cartas digitales
		setTimeout(() => {

			if (this.textAreaElement.current === null)
				return;

			const themeSetup = EditorView.theme({
				'&': {
					border: '1px solid #eee'
				},
				'&.cm-editor.cm-focused': {
					outline: 'none'
				}
			});

			// Migration Guide from textAarea: https://codemirror.net/docs/migration/
			const extensionSetup = this.props.mode === 'xml' ? xml() : css();
			this.codeMirrorEditor = new EditorView({ doc: this.textAreaElement.current.value, extensions: [themeSetup, basicSetup, extensionSetup] });
			this.textAreaElement.current.parentNode!.insertBefore(this.codeMirrorEditor.dom, this.textAreaElement.current!);
			this.textAreaElement.current.style.display = 'none';
			if (this.textAreaElement.current.form) this.textAreaElement.current!.form.addEventListener('submit', () => {
				this.textAreaElement.current!.value = this.codeMirrorEditor!.state.doc.toString();
			});
		}, 100);
	}

	render() {

		return (
			<div className={this.state.errorMsg ? 'has-error' : ''}>
				<ErrorBlock msg={this.state.errorMsg} />
				<textarea
					id={formatAsElementId(this.getPath(), this.props.formId || '')}
					ref={this.textAreaElement}
					defaultValue={this.state.value} />
			</div>
		);
	}
}

export type CodeEditorCtor<T> = new (props: Props<T>) => CodeEditor<T>;